import React, { useState, useEffect } from 'react';
import { Modal, ScrollView } from 'react-native';
import { FooterText, SubTitle, StandardText } from "../../../config/theme/globalStyles";
import AdminButton from "../../../components/AdminButton";
import { ViewTextInput, ViewButton, ModalBackground, ModalContainer, Header, CloseButton, Content } from "./style";
import { getProductById } from '../../../services/guru';
import CreateProductGenericForm from "./CreateProductGenericForm";
import { createNewProduct } from "./service/product-service";
import { useRemoteConfigContext } from '../../../contexts/useRemoteConfigContext';
import { ADMIN_COLORS } from "../../../config/theme/adminColors";
import AdminAlert from "../../../components/AdminAlert";
import AdminInput from "../../../components/AdminInput";

const CreateProductModal = ({ isVisible, onClose, onCreate }) => {
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [productId, setProductId] = useState('');
  const [productInfo, setProductInfo] = useState('');
  const [offersInfos, setOffersInfos] = useState('');
  const [saveButtonVisible, setSaveButtonVisible] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const config = useRemoteConfigContext();

  const [alertState, setAlertState] = useState({
    visible: false,
    message: null,
  });

  const showAlert = (message) => {
    setAlertState({ visible: true, message });
  };

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  const handleCreate = async () => {
    try {
      setLoadingSave(true);
      const result = await onCreate(productInfo, offersInfos, productId);
      if (result.success) {
        showAlert("Novo produto adicionado com sucesso!");
        setShowSuccessMessage(true);
        onClose();
      } else if (result.error) {
        showAlert(result.error);
      }
    } catch (error) {
      console.error('Erro ao criar produto:', error);
      showAlert('Erro ao criar produto');
    } finally {
      setLoadingSave(false);
    }
  };

  const guruErrorMessage = (error) => {
    const details =
      error?.details ||
      error?.data ||
      error?.customData ||  
      error?.customData?.details ||
      error?.response?.data;

    const reason = details?.reason;
    const guruMessage = details?.guruMessage;

    if ((error?.message || "").toLowerCase().includes("product not found")) {
      return "Produto não encontrado na Guru. Confirme se o ID informado é o *marketplace_id* (código do marketplace).";
    }

    switch (reason) {
      case "GURU_ACCOUNT_DISABLED":
        return "Integração indisponível: sua conta na Guru está desativada. Reative a conta na Guru (ou contate o suporte) e tente novamente.";
      case "GURU_UNAUTHORIZED":
        return "Integração não autorizada: o token da Guru é inválido/expirou. Gere um novo token na Guru e atualize no painel.";
      case "GURU_FORBIDDEN":
        return "Sem permissão na Guru para consultar produtos. Verifique se o usuário do token tem acesso e permissões de API.";
      case "GURU_RATE_LIMIT":
        return "Muitas requisições em sequência. Aguarde alguns instantes e tente novamente.";
      case "GURU_INVALID_PARAMS":
        return `Dados inválidos enviados para a Guru. ${guruMessage ? `Detalhe: ${guruMessage}` : "Revise o ID informado e tente novamente."}`;
      case "GURU_NOT_FOUND":
        return "Recurso não encontrado na Guru. Confirme se o ID informado é o *marketplace_id* e se o produto realmente existe.";
      case "GURU_INTERNAL":
        return "A Guru está instável no momento. Tente novamente mais tarde.";
      case "GURU_INVALID_JSON":
        return "A Guru retornou uma resposta inesperada. Tente novamente. Se persistir, acione o suporte.";
      default:
        return "Não foi possível consultar a Guru agora. Tente novamente. Se persistir, acione o suporte.";
    }
  };

  const findPlanAndOffers = async () => {
    if (!productId.trim()) {
      showAlert("Por favor, digite o ID do produto");
      return;
    }

    try {
      setLoadingSearch(true);
      const infos = await getProductById(productId);

      if (!infos || !infos.product) {
        throw new Error("Product not found");
      }

      setAlertState({ visible: false, message: null });

      setProductInfo(infos.product);
      setOffersInfos(infos.offers);
      setSaveButtonVisible(true);
    } catch (error) {
      console.error("Erro ao buscar produto na Guru:", error);
      showAlert(guruErrorMessage(error));
    } finally {
      setLoadingSearch(false);
    }
  };


  return (
    <Modal
      animationType="fade"
      isVisible={isVisible}
      onBackdropPress={onClose}
      transparent={true}
    >
      <ModalBackground>
        <ModalContainer>
          <ScrollView
            showsVerticalScrollIndicator={true}
            style={{ width: '100%', height: '100%' }}
          >
            <Header>
              {!saveButtonVisible && (
                <SubTitle padding="0.2rem 0rem" color={ADMIN_COLORS.ink}>
                  Adicionar Produto no aplicativo:
                </SubTitle>
              )}
              <CloseButton onPress={onClose}>
                <StandardText color={ADMIN_COLORS.ink}>X</StandardText>
              </CloseButton>
            </Header>

            {config.paymentGateway === "PAGARME" ? (
              <CreateProductGenericForm onClose={onClose} saveHandler={createNewProduct}></CreateProductGenericForm>
            ) : (
              <Content>
                <ViewTextInput>
                      <AdminInput
                        placeholder="Digite o ID do Produto"
                        value={productId}
                        onChangeText={(id) => {
                      setProductId(id);
                      if (id.trim()) {
                        setAlertState({ visible: false, message: null });
                      }
                    }}
                    />
                  <FooterText padding="0.2rem 0rem" color={ADMIN_COLORS.ink}>
                    {!saveButtonVisible
                      ? "O ID do produto deve ser o código gerado na Guru Digital"
                      : "Produto encontrado com sucesso!"}
                  </FooterText>
                  {alertState.visible && (
                    <AdminAlert variant="error">{alertState.message}</AdminAlert>
                  )}
                </ViewTextInput>
                <ViewButton>
                  {!saveButtonVisible &&
                    <AdminButton
                      title="Buscar informações do produto"
                      icon="search-outline"
                      isLoading={loadingSearch}
                      onPress={findPlanAndOffers}
                    />
                  }
                </ViewButton>
              </Content>
            )}
            {/* Secundário à esquerda, principal à direita. O gap substitui a
                antiga `margin` no segundo botão. */}
            {saveButtonVisible && (
              <ViewButton style={{ marginTop: '1rem', gap: 12 }}>
                <AdminButton
                  title="Cancelar"
                  variant="secondary"
                  onPress={onClose}
                />
                <AdminButton
                  title="Salvar"
                  isLoading={loadingSave}
                  onPress={handleCreate}
                />
              </ViewButton>
            )}
          </ScrollView>
        </ModalContainer>
      </ModalBackground>
    </Modal>
  );
};


export default CreateProductModal;